import React from "react";
import { Check, Circle } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import { accentClass } from "./stepAccents";
import StepHeader from "./StepHeader";

// Final confirmation. Summarises the checks that were actually run — the rows
// come from the live step list, so they track the org's config rather than a
// hardcoded set.
// The countdown is not handled here — once it starts, InterviewInstructions
// swaps the whole screen for the fullscreen count, so this step unmounts.
const ReadyStep = ({
  completedSteps = [],
  // Optional round-specific rules shown under the summary (the coding flow
  // uses this for its challenge rules).
  checklist,
  onStart,
  onBack,
  stepNumber,
  totalSteps,
  headline = "Cleared to begin",
  startButtonLabel = "Start Interview",
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const cardClass = isLight
    ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm";

  return (
    <div className="w-full">
      <StepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="You're all set"
        description="Everything checks out. Take a breath — the interview will begin as soon as you're ready."
      />

      <div className={`mt-7 rounded-2xl border ${cardClass}`}>
        <div className="px-6 py-8">
          <div className="flex flex-col items-center">
            <span className="w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-[#2F64FF] to-[#FB54AE]">
              <Check className="w-7 h-7 text-white" strokeWidth={3} />
            </span>

            <h2
              className={`text-[16px] font-medium mt-4 ${
                isLight ? "text-slate-800" : "text-white"
              }`}
            >
              {headline}
            </h2>
          </div>

          {completedSteps.length > 0 && (
            <>
              <hr
                className={`my-6 ${
                  isLight ? "border-[#F1F2F7]" : "border-gray-700/50"
                }`}
              />

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {completedSteps.map((step) => {
                  const Icon = step.icon || Circle;
                  return (
                    <li key={step.id} className="flex items-center gap-3">
                      <span
                        className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${accentClass(
                          step.accent,
                          isLight
                        )}`}
                      >
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      </span>
                      <span
                        className={`text-[13px] ${
                          isLight ? "text-slate-700" : "text-gray-200"
                        }`}
                      >
                        {step.doneLabel || step.title}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {checklist?.length > 0 && (
            <>
              <hr
                className={`my-6 ${
                  isLight ? "border-[#F1F2F7]" : "border-gray-700/50"
                }`}
              />
              <ul className="space-y-2.5">
                {checklist.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span
                      className={`mt-[7px] w-1 h-1 rounded-full shrink-0 ${
                        isLight ? "bg-slate-300" : "bg-gray-500"
                      }`}
                    />
                    <span
                      className={`text-[13px] leading-relaxed ${
                        isLight ? "text-slate-600" : "text-gray-300"
                      }`}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={onBack}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium border transition-colors ${
            isLight
              ? "bg-white border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
              : "bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700/40"
          }`}
        >
          Back
        </button>

        <button
          onClick={onStart}
          className="px-7 py-2.5 rounded-full text-[14px] font-medium text-white transition-opacity bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
        >
          {startButtonLabel}
        </button>
      </div>
    </div>
  );
};

export default ReadyStep;
